import config from 'constants/config'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import createSelector from 'lib/web/createSelector'
import { isEnabledForFeature, isFeatureFlagIncluded } from 'lib/config/featureFlagUtils'
import { OptimizelyClientSideExperiments, OptimizelyServerSideExperiments } from 'lib/optimizely/optimizelyUtils'
import { geoCriteriaMetForCalendarRecommendations } from 'lib/customer/recommendationUtils'
import { isStripe3dsEnabled } from 'lib/payment/stripeUtils'
import { removeRegionFromPath } from 'lib/url/searchUrlUtils'
import { isHeadlessModeSel, isMobileSel } from './configSelectors'
import memoize from 'lib/memoize/memoize'
import { getFlightItems } from 'checkout/selectors/view/flights'

export const shouldEnableCalendarRecommendations = createSelector(
  (state: App.State, offer: App.Offer | App.BedbankOffer | undefined) => offer,
  (offer) => offer?.type !== 'rental' && geoCriteriaMetForCalendarRecommendations(offer),
)

export const isStripe3dsAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (currentCurrency): boolean => (isStripe3dsEnabled(currentCurrency)),
)

export const isSpoofed = createSelector(
  (state: App.State) => state.auth.account.isSpoofed,
  (isSpoofed) => isSpoofed,
)

export const isCustomerSupportMode = createSelector(
  isSpoofed,
  (state: App.State) => state.config.previewMode,
  (state: App.State) => state.config.storeMode,
  (isSpoofed, isPreviewMode, isStoreMode) => isSpoofed || isPreviewMode || isStoreMode,
)

export const shouldShowSalesUSP = createSelector(
  (_state: App.State, lowestPricedPackageTrackingPrice: number, threshold: number) => lowestPricedPackageTrackingPrice >= threshold,
  (state: App.State) => state.geo.currentRegionCode,
  (isPriceOverThreshold, currentRegion): boolean => isEnabledForFeature('ALL/IN', currentRegion) && isPriceOverThreshold,
)

export const multiRoomEnabled = createSelector(
  (_state: App.State, offer: App.Offer) => offer?.type,
  (offerType) => config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED && isEnabledForFeature(config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED_LIST, offerType),
)

export const shouldShowContinueToApp = createSelector(
  isMobileSel,
  (state: App.State) => state.geo.currentRegionCode,
  isHeadlessModeSel,
  (state: App.State) => state.config.appBannerCookie,
  (state: App.State) => state.utm.medium,
  (state: App.State) => !!state.router.location.query.gclid,
  (state: App.State) => state.config.originalCanonicalURL,
  (isMobileSel, regionCode, isHeadlessModeSel, appBannerCookie, utmMedium, hasGoogleClickIdentifier, url): boolean =>
    config.CONTINUE_TO_APP_BANNER &&
      isMobileSel &&
      !isHeadlessModeSel &&
      !appBannerCookie &&
      (utmMedium !== 'cpc' || (utmMedium === 'cpc' ||
        hasGoogleClickIdentifier) &&
        !!config.appBanner?.promotionData[regionCode]?.specialCodeForAdClicks) &&
      !url.includes('top-picks-for-me'),
)

export const getInstalmentsAbTestEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.PAYMENTS_INSTALMENTS_EXPERIMENT_ID],
  (experiment) => {
    return experiment === '1' || experiment === '2'
  },
)

export const newOfferTileAndLandingPageEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_NEW_OFFER_TILE_AND_LANDING_PAGE],
  (experiment) => {
    return experiment === '1'
  },
)

export const cruiseSupportBanner = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_CRUISE_SUPPORT_BANNER_EXPERIMENT_ID],
  (experiment) => {
    return experiment === '1'
  },
)

export const toursPricePerDayABTestVariation = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_TOURS_PRICE_PER_DAY],
  (experiment) => experiment,
)

export const isReserveVsPayWordingForDepositsEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_RESERVE_VS_PAY_WORDING_FOR_DEPOSITS_EXPERIMENT_ID],
  (experiment): boolean => {
    return experiment === '1'
  },
)

export const isOfferPageCuratedTripBannerEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_CURATED_TRIPS_OFFER_PAGE_PROMOTION],
  (experimentValue): boolean => {
    return experimentValue === '1'
  },
)

export const hotelSearchPageCuratedTripBannerPosition = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_CURATED_TRIPS_HOTEL_SEARCH_PAGE_PROMOTION],
  (experimentValue): number | undefined => {
    if (experimentValue === '1') { return 3 }
    if (experimentValue === '2') { return 5 }
  },
)

export const isIntroMapEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_MAP_POSITION],
  (experimentValue): boolean => {
    return experimentValue === '1'
  },
)

export const isVelocityEnabled = createSelector(
  (state: App.State) => state.geo.partnership?.code,
  (partnershipCode): boolean => partnershipCode === VIRGIN_VELOCITY_PARTNERSHIP_CODE && !!config.VELOCITY_SSO_URL,
)

export const isManualSortingEnabledFor = memoize((listName: string) => {
  return isEnabledForFeature(config.MANUAL_SORTING_ENABLED, listName)
})

export const isSupportAssistantEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.config.optimizelyExperiments[OptimizelyServerSideExperiments.isSupportAssistantEnabled],
  (currentRegionCode, supportAssistantFlag) => {
    return supportAssistantFlag?.variationKey === 'on' &&
      config.SUPPORT_ASSISTANT_ENABLED &&
      isFeatureFlagIncluded(config.SUPPORT_ASSISTANT_REGIONS, currentRegionCode)
  },
)

export const isDynamicSearchFooterBoxContent = createSelector(
  (state:App.State) => state.router.location.pathname,
  (state: App.State) => state.content,
  (path, content) => {
    const pathWithoutRegion = removeRegionFromPath(path)
    return config.searchFooterBoxType === 'from-contentful' &&
        'searchFooterLinkBox' in content &&
        content.searchFooterLinkBox[pathWithoutRegion] !== undefined &&
        Object.keys(content.searchFooterLinkBox[pathWithoutRegion] || {}).length > 0
  },
)

export const isHomepageSupported = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (currentRegion): boolean => isEnabledForFeature(config.HOME_PAGE_TEST_REGIONS, currentRegion),
)

export const isMerchantFeeEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.config.optimizelyExperiments[OptimizelyClientSideExperiments.paymentsMerchantFees]?.enabled,
  (state: App.State) => state.auth.account.isSpoofed,
  getFlightItems,
  (currentRegionCode, isMerchantFeeABTestEnabled, isSpoofed, flightItems) =>
    config.MERCHANT_FEE_ENABLED &&
    currentRegionCode === 'AU' &&
    // These should be removed POST AB test
    !isSpoofed &&
    !(flightItems?.length > 0) &&
    isMerchantFeeABTestEnabled
  ,
)

export const isHomePageEnabled = createSelector(
  (state: App.State) => state.config.optimizelyExperiments[OptimizelyServerSideExperiments.isNewHomePageTestForDesktopEnabled].enabled,
  (state: App.State) => state.config.optimizelyExperiments[OptimizelyServerSideExperiments.isAuNewHomePageTestForDesktopEnabled].enabled,
  (state: App.State) => state.config.optimizelyExperiments[OptimizelyServerSideExperiments.isNewHomePageTestForMobileEnabled].enabled,
  (state: App.State) => state.config.optimizelyExperiments[OptimizelyServerSideExperiments.isAuNewHomePageTestForMobileEnabled].enabled,
  (desktopInternational, desktopDomestic, mobileInternational, mobileDomestic) => {
    return desktopInternational || desktopDomestic || mobileInternational || mobileDomestic
  },
)

export const isMerchantFeeFlightsEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.config.optimizelyExperiments[OptimizelyClientSideExperiments.paymentsFlightsMerchantFees]?.enabled,
  (state: App.State) => state.auth.account.isSpoofed,
  getFlightItems,
  (currentRegionCode, isMerchantFeeFlightsABTestEnabled, isSpoofed, flightItems) =>
    config.MERCHANT_FEE_ENABLED &&
    currentRegionCode === 'AU' &&
    // These should be removed POST AB test
    !isSpoofed &&
    (flightItems?.length > 0) &&
    isMerchantFeeFlightsABTestEnabled
  ,
)
